import * as React from 'react';
import {
  dtoToAppDependenciesProps,
  dtoToSolutionPageProps,
  SolutionPage,
  SolutionPageCtx,
} from '.';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import type { MarketplaceStore } from '../../component-decorators/with-marketplace-store';
import {
  useMarketplaceStore,
  withMarketplaceStore,
} from '../../component-decorators/with-marketplace-store';
import {
  getHasOrdersInOldSystem,
  getSolutionBySlug,
  getSolutionPreviewBySlugOrId,
} from '../../api/api';
import { biLogger } from '../../bi';
import { config } from '../../config';
import s from './solution-page-views/solution-page.scss';
import { PreviewMode } from './preview-elements';
import { HeaderContext } from './solution-page-views/solution-page-header/header-context';
import { SolutionPageHeader } from './solution-page-views/solution-page-header/solution-page-header';
import { dtoToDetailsProps } from './solution-page-views';
import { AppPageMode, Origin, Path } from '../..';
import type { IFetcherInjected } from '../../component-decorators/with-fetcher';
import { WithFetcher } from '../../component-decorators/with-fetcher';
import { createTestAppData } from './test-app-defaults';
import { composer } from '../../component-decorators/composer';
import { CenteredLoader } from '../centered-loader';
import { SolutionPageSEO } from './solution-page-seo';
import {
  getPathForBi,
  getReferralInfo,
  getWidgetReferral,
} from '../../common/bi-events';
import { useConfigContext } from '../../config-context';
import { ErrorPageContainer } from '../error-page/error-page-container';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import type { WebSolution, WSTag } from '@wix/ambassador-marketplace/types';
import type { AppMarketRpcClient } from '../../types/app-market-rpc-client';
import { appMarketPageView } from '@wix/bi-logger-market/v2';
import { Logger } from '@wix/wix-log';
import { AppGroup } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import {
  appNotSupportedInEditorX,
  appNotSupportedInStudio,
} from '../../common/utils';
import { queryTag } from '@wix/ambassador-prime-auto-v1-tag/http';
import type {
  QueryTagRequest,
  QueryTagResponse,
} from '@wix/ambassador-prime-auto-v1-tag/types';
import { httpClient } from '../../http-client';

export interface ISolutionPageContainerProps
  extends Partial<WithTranslationProps> {
  slug: string;
  mode?: AppPageMode;
  version?: string;
  previewLangCode?: string;
  locale: string;
  isMobile: boolean;
}

interface ISolutionPageContainerLogic
  extends ISolutionPageContainerProps,
    IFetcherInjected,
    Partial<WithTranslationProps>,
    Partial<InjectedExperimentsProps> {
  marketplaceStore?: MarketplaceStore;
}

@withMarketplaceStore
class SolutionPageContainerLogic extends React.Component<
  ISolutionPageContainerLogic,
  { showHeader: boolean }
> {
  constructor(props) {
    super(props);
    this.state = { showHeader: false };
  }

  showHeader = () => {
    this.setState({ showHeader: true });
  };
  hideHeader = () => {
    this.setState({ showHeader: false });
  };

  componentDidUpdate(prevProps: ISolutionPageContainerLogic) {
    prevProps.slug !== this.props.slug &&
      this.props.reFetch(() =>
        getData(
          this.props.t,
          this.props.slug,
          this.props.mode,
          this.props.previewLangCode,
          this.props.locale,
          '',
          this.props.experiments,
        ),
      );
    prevProps.fetchData !== this.props.fetchData &&
      this.sendPageViewBi(this.props.fetchData);
  }

  componentDidMount() {
    this.props.fetchData && this.sendPageViewBi(this.props.fetchData);
  }

  render() {
    const prevRoute = this.props.marketplaceStore.prevRoute;
    const addAppStickyStripPosition = 'top';
    const positionTop = '-73px';
    const appDependencies = dtoToAppDependenciesProps(
      this.props.fetchData,
      this.props.marketplaceStore.installedApps,
      config.origin,
    );
    const isInTestFlow =
      this.props.marketplaceStore?.route?.appPageMode === AppPageMode.TEST;
    const isNotSupportedInEditorX =
      appNotSupportedInEditorX(
        this.props?.fetchData?.baseInfo,
        this.props.experiments,
      ) && !isInTestFlow;
    const isNotSupportedInStudio =
      appNotSupportedInStudio(
        this.props?.fetchData?.baseInfo,
        this.props.experiments,
      ) && !isInTestFlow;
    const ORDERS_APP_ID = '9a5d83fd-8570-482e-81ab-cfa88942ee60';
    const isOrders = this.props?.fetchData?.baseInfo.id === ORDERS_APP_ID;
    const isOrdersInstalled = !!this.props.marketplaceStore.installedApps?.find(
      (installedApp) => installedApp.appId === ORDERS_APP_ID,
    );
    const preventNewOrdersInstallation =
      isOrders &&
      !isOrdersInstalled &&
      this.props.experiments.enabled(
        'specs.marketplace.preventNewOrdersInstallation',
      ) &&
      this.props?.fetchData?.hasOrdersInOldSystem;
    const isNotAvailableToInstall =
      isNotSupportedInEditorX || isNotSupportedInStudio;
    return (
      <div>
        {addAppStickyStripPosition === 'top' ? (
          <SolutionPageHeader
            {...dtoToDetailsProps(this.props.fetchData)}
            show={this.state.showHeader}
            position="top"
            isMobile={this.props.isMobile}
            appId={this.props?.fetchData?.baseInfo?.id}
            appDependencies={appDependencies}
            isNotAvailableToInstall={isNotAvailableToInstall}
          />
        ) : null}
        <div
          style={{ position: 'relative', top: positionTop }}
          data-testid={`solution-${this.props.slug}`}
        >
          <div>
            <HeaderContext.Provider
              value={{
                showHeader: this.showHeader,
                hideHeader: this.hideHeader,
              }}
            >
              <div className={s.backgroundSolutionPage}>
                <PreviewMode.Provider
                  value={{
                    appId: this.props.fetchData.baseInfo.id,
                    isPreview: this.props.mode === AppPageMode.PREVIEW,
                  }}
                >
                  <SolutionPageCtx.Provider
                    value={{ app: this.props.fetchData }}
                  >
                    <SolutionPage
                      {...dtoToSolutionPageProps(
                        this.props.fetchData,
                        prevRoute,
                        this.props.mode,
                        this.props.marketplaceStore.installedApps,
                        config.origin,
                        isNotAvailableToInstall,
                        preventNewOrdersInstallation,
                      )}
                    />
                  </SolutionPageCtx.Provider>
                </PreviewMode.Provider>
              </div>
            </HeaderContext.Provider>
          </div>
        </div>
      </div>
    );
  }

  sendPageViewBi(solutionDto: WebSolution): void {
    const logger = new Logger('require-app-id');
    const pageViewParams = {
      app_id: solutionDto.baseInfo.id,
      esi: config.esi,
      market: config.originForBI,
      navigation_source: this.props.marketplaceStore?.route?.searchLocation,
      tag_type: getPathForBi(Path.WEB_SOLUTION),
      referral_info: getReferralInfo(
        this.props.marketplaceStore.route &&
          this.props.marketplaceStore.route.referral,
        this.props.marketplaceStore.prevRoute,
      ),
      search_term:
        this.props.marketplaceStore.prevRoute &&
        this.props.marketplaceStore.prevRoute.query,
      widget_referral: getWidgetReferral(),
      referral_section_index: this.props.marketplaceStore?.route?.appIndex,
      referral_section_name: this.props.marketplaceStore?.route
        ?.referralSectionName
        ? this.props.marketplaceStore?.route?.referralSectionName
        : this.props.marketplaceStore?.route?.referral,
      slug_id: this.props.marketplaceStore?.route?.referralSectionName,
      referral_tag_name: this.props.marketplaceStore?.prevRoute?.slug?.replace(
        /-/g,
        '_',
      ),
      referral_tag_type: getPathForBi(
        this.props.marketplaceStore?.prevRoute?.path,
      ),
      referral_sub_tag_name:
        this.props.marketplaceStore?.prevRoute?.subCategories?.join(','),
      collimp_id: this.props.marketplaceStore?.route?.collimp_id,
      section: this.props.marketplaceStore?.route?.section,
      utm_id: this.props.marketplaceStore?.route?.utm_id,
      app_review_score: solutionDto.baseInfo?.reviewStars
        ? Number((solutionDto.baseInfo?.reviewStars * 100).toFixed(2))
        : -1000,
      app_review_count: solutionDto.baseInfo?.numberOfReviews,
      is_app_dependency: solutionDto.requiredApps.length > 0,
      is_compatible: solutionDto?.requiredApps?.some((requiredApp) =>
        this.props.marketplaceStore.installedApps?.some(
          (installedApp) =>
            installedApp.appId === requiredApp.id &&
            (installedApp.installationDetails.group === AppGroup.INSTALLED ||
              installedApp.installationDetails.group ===
                AppGroup.BUSINESS_SOLUTIONS),
        ),
      ),
      is_external:
        this.props?.marketplaceStore?.route?.referral &&
        this.props?.marketplaceStore?.route?.referral !== 'manage_apps',
    };

    biLogger.report(appMarketPageView(pageViewParams));
    logger.info(
      'App page view event with app review score field with params: ',
      pageViewParams,
    );
  }
}

const fetchSolutionBySlug = (
  t,
  slug,
  mode,
  previewLangCode,
  locale,
  experiments,
  rpcClient?: AppMarketRpcClient,
) => {
  if (mode === AppPageMode.TEST) {
    return getSolutionPreviewBySlugOrId(
      true,
      slug,
      previewLangCode,
      experiments,
      rpcClient,
    ).then((data) => {
      return createTestAppData(t, data);
    });
  }
  if (mode === AppPageMode.PREVIEW) {
    return getSolutionPreviewBySlugOrId(
      true,
      slug,
      previewLangCode,
      experiments,
      rpcClient,
    ).then((data) => data);
  }
  return getSolutionBySlug(slug, locale, experiments, rpcClient);
};

const fetchHasOrdersInOldSystem = (metasiteId: string, experiments) => {
  return experiments.enabled(
    'specs.marketplace.preventNewOrdersInstallation',
  ) && metasiteId
    ? getHasOrdersInOldSystem(metasiteId).then(
        (data) => data.hasOrdersInOldSystem,
      )
    : undefined;
};

const fetchCategories = async ({
  experiments,
  tags,
  locale,
  rpcClient,
}: {
  experiments: InjectedExperimentsProps['experiments'];
  tags: WSTag[];
  locale: string;
  rpcClient?: AppMarketRpcClient;
}): Promise<
  {
    slug: string;
    name: string;
  }[]
> => {
  const newTagsBackendExperimentEnabled = experiments?.enabled?.(
    'com.wixpress.devcenter.market.specs.ShouldUseNewTagsService',
  );

  if (!newTagsBackendExperimentEnabled) {
    return [];
  }

  const subCategoryTags = tags.filter((tag) => {
    return tag.type === 'CATEGORY' && tag.parentSlug && !tag.isHidden;
  });
  const categorySlugs = subCategoryTags.map((tag) => tag.parentSlug);

  if (categorySlugs.length === 0) {
    return [];
  }

  const queryTagRequest: QueryTagRequest = {
    query: {
      filter: {
        slug: categorySlugs,
      },
    },
    languageCode: locale,
  };
  let queryTagResponse: QueryTagResponse;
  const isSSR = typeof window === 'undefined';
  if (isSSR && rpcClient !== undefined) {
    queryTagResponse = await rpcClient.queryTag(queryTagRequest);
  } else {
    const { data } = await httpClient.request(queryTag(queryTagRequest));
    queryTagResponse = data;
  }

  return categorySlugs.map((slug) => {
    const matchingTag = (queryTagResponse?.tags ?? []).find(
      (tag) => tag.slug === slug,
    );
    return {
      slug,
      name: matchingTag?.content?.name ?? matchingTag?.name ?? '',
    };
  });
};

const getData = async (
  t,
  slug,
  mode,
  previewLangCode,
  locale,
  metaSiteId,
  experiments,
  signedInstance?: string,
  rpcClient?: AppMarketRpcClient,
) => {
  const [solutionBySlug, hasOrdersInOldSystem] = await Promise.all([
    fetchSolutionBySlug(
      t,
      slug,
      mode,
      previewLangCode,
      locale,
      experiments,
      rpcClient,
    ),
    fetchHasOrdersInOldSystem(metaSiteId, experiments),
  ]);
  const categories = await fetchCategories({
    experiments,
    tags: solutionBySlug?.baseInfo?.Tags ?? [],
    locale,
    rpcClient,
  });

  return {
    ...solutionBySlug,
    hasOrdersInOldSystem,
    categories,
  };
};

export const SolutionPageContainer = composer()
  .withTranslation()
  .withExperiments()
  .compose((props) => {
    const { locale, isMobile, rpcClient, getAppInstance } = useConfigContext();
    const signedInstance = getAppInstance
      ? getAppInstance('22bef345-3c5b-4c18-b782-74d4085112ff')
      : undefined;
    const { metaSiteId } = useMarketplaceStore();

    return (
      <WithFetcher
        loader={<CenteredLoader />}
        initialFetch={() =>
          getData(
            props.t,
            props.slug,
            props.mode,
            props.previewLangCode,
            locale,
            metaSiteId,
            props.experiments,
            signedInstance,
            rpcClient,
          )
        }
        fetchMemoizationId={`solution-${props.slug}-${locale}`}
      >
        {({ fetchData, reFetch }) => {
          if (fetchData && fetchData.baseInfo) {
            return (
              <>
                {config.origin === Origin.STANDALONE &&
                  props.mode !== AppPageMode.TEST &&
                  props.mode !== AppPageMode.PREVIEW && (
                    <SolutionPageSEO
                      appName={fetchData.baseInfo.name}
                      teaser={fetchData.baseInfo.teaser}
                      logo={fetchData.baseInfo.icon}
                      appSlug={props.slug}
                      ratingValue={fetchData.baseInfo.reviewStars}
                      ratingCount={fetchData.baseInfo.numberOfReviews}
                      benefits={fetchData.webSolutionData.appData.benefits}
                    />
                  )}
                <SolutionPageContainerLogic
                  {...props}
                  fetchData={fetchData}
                  reFetch={reFetch}
                  locale={locale}
                  isMobile={isMobile}
                />
              </>
            );
          }
          return <ErrorPageContainer slug={props.slug} />;
        }}
      </WithFetcher>
    );
  });
